import { useEffect, useRef, useState } from 'react';

/**
 * Marks a container visible once it enters the viewport.
 *
 * The previous version wrapped a whole section, so a tall section popped
 * in as one block. The CSS now animates the container's *children*, and
 * `staggerProps` gives each child an increasing delay, capped so a long
 * list never leaves the last item hanging.
 */
export const useScrollReveal = ({ threshold = 0.05, rootMargin = '0px 0px -5% 0px' } = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setIsVisible(true);
        observer.disconnect();
      },
      { threshold, rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  return {
    ref,
    isVisible,
    className: `reveal ${isVisible ? 'is-visible' : ''}`,
  };
};

const STAGGER_MS = 40;
const MAX_STEPS = 6;

export const staggerProps = (index) => ({
  style: { '--reveal-delay': `${Math.min(index, MAX_STEPS) * STAGGER_MS}ms` },
});

export default useScrollReveal;
